import { useState } from 'react';
import { B, radius } from '../constants/brand';
import { fmt } from '../utils/format';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Input, Select, Modal, ModalFooter, Spinner, ErrorMsg, SectionHeader, Card, Table, Badge, Tabs, InfoGrid, StatusBadge, useToast, PageHeader, Avatar, FieldLabel, Icon } from '../components/ui';

export const Users = () => {
  const { toast } = useToast();
  const { data: users, loading, error, reload } = useApi(() => api.get("users"), []);
  const [selected, setSelected]       = useState(null);
  const [activeTab, setActiveTab]     = useState("Profile");
  const [activity, setActivity]       = useState(null);
  const [showEntra, setShowEntra]     = useState(false);
  const [editing, setEditing]         = useState(null);
  const [editForm, setEditForm]       = useState({});
  const [newForm, setNewForm]         = useState({});
  const [saving, setSaving]           = useState(false);
  const [activityError, setActivityError] = useState(null);

  const openDetail = async (u) => {
    setSelected(u); setActiveTab("Profile"); setActivity(null); setActivityError(null);
    // A swallowed error left `activity` null, which the Activity tab renders as
    // a Spinner — so a failing request presented as "still loading", forever.
    try { const detail = await api.get(`users/${u.userID}`); setActivity(detail.activity || null); }
    catch (e) { setActivityError(e.message || 'Could not load this user\u2019s activity.'); }
  };

  const startEdit  = u => {
    setEditing(u.userID);
    setEditForm({
      displayName:     u.displayName,
      role:            u.role,
      isInternal:      u.isInternal,
      isActive:        u.isActive,
      phoneNumber:     u.phoneNumber     || '',
      smsEnabled:      !!u.smsEnabled,
      teamsUserId:     u.teamsUserId     || '',
    });
  };
  const saveEdit   = async () => {
    setSaving(true);
    try { await api.put(`users/${editing}`, editForm); setEditing(null); reload(); }
    catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.displayName}?`)) return;
    try { await api.del(`users/${u.userID}`); reload(); }
    catch (e) { toast.error(e.message); }
  };


  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  return (
    <div>
      <PageHeader
        icon="users"
        title="Users"
        actions={<Btn variant="ghost" onClick={() => setShowEntra(true)}>Entra Directory</Btn>}
      />

      <Card style={{ padding: 0 }}>
        <Table
          columns={[
            { key: "displayName", label: "Name", render: (v, row) => (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Avatar initials={fmt.initials(v)} color={row.isInternal ? B.navy : B.tealDk} />
                <div>
                  <div style={{ fontWeight: 600 }}>{v}</div>
                  <div style={{ fontSize: 11, color: B.textLt }}>{row.email}</div>
                </div>
              </div>
            )},
            { key: "role",       label: "Role",   render: v => <Badge color={v === "Admin" ? B.navy : B.tealDk}>{v}</Badge> },
            { key: "isInternal", label: "Type",   render: v => <Badge color={v ? B.navy : B.textMd}>{v ? "Internal" : "External"}</Badge> },
            { key: "isActive",   label: "Status", render: v => <StatusBadge status={v ? "Active" : "Inactive"} /> },
            { key: "actions", label: "", render: (_, row) => (
              <div style={{ display: "flex", gap: 6 }}>
                <Btn size="sm" variant="ghost"      onClick={() => openDetail(row)}>View</Btn>
                <Btn size="sm" variant="secondary"  onClick={() => startEdit(row)}>Edit</Btn>
                {row.isActive && <Btn size="sm" variant="danger" onClick={() => deactivate(row)}>Deactivate</Btn>}
              </div>
            )},
          ]}
          data={users}
        />
      </Card>

      {/* User Detail */}
      {selected && (
        <Modal title={selected.displayName} onClose={() => { setSelected(null); setActivity(null); }} width={700}>
          <Tabs tabs={["Profile","Activity"]} active={activeTab} onChange={setActiveTab} />

          {activeTab === "Profile" && (
            <InfoGrid items={[
              ["Email",    selected.email],
              ["Role",     <Badge color={selected.role === "Admin" ? B.navy : B.tealDk}>{selected.role}</Badge>],
              ["Type",     selected.isInternal ? "Internal" : "External"],
              ["Status",   <StatusBadge status={selected.isActive ? "Active" : "Inactive"} />],
              ["Auth",     "Entra ID"],
            ]} />
          )}

          {activeTab === "Activity" && (
            activityError
              ? <div style={{ fontSize: 13, color: B.red }}>{activityError}</div>
              : activity
              ? <div>
                  <SectionHeader title="Open Tasks" />
                  {(!activity.tasks || activity.tasks.length === 0) ? <div style={{ fontSize: 13, color: B.textLt, marginBottom: 16 }}>No open tasks.</div>
                    : activity.tasks.map(t => (
                      <div key={t.taskID} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${B.border}` }}>
                        <span style={{ fontSize: 13 }}>{t.title}</span>
                        <StatusBadge status={t.priority} />
                      </div>
                    ))
                  }
                  <SectionHeader title="Deals" />
                  {(!activity.deals || activity.deals.length === 0) ? <div style={{ fontSize: 13, color: B.textLt, marginBottom: 16 }}>No deals.</div>
                    : activity.deals.map(p => (
                      <div key={p.dealID} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${B.border}` }}>
                        <span style={{ fontSize: 13 }}>{p.name}</span>
                        <StatusBadge status={p.status} />
                      </div>
                    ))
                  }
                </div>
              : <Spinner />
          )}
        </Modal>
      )}

      {/* Edit User */}
      {editing && (
        <Modal title="Edit User" onClose={() => setEditing(null)}>
          <Input label="Display Name" value={editForm.displayName} onChange={v => setEditForm(p => ({ ...p, displayName: v }))} />
          <Select label="Role" value={editForm.role} onChange={v => setEditForm(p => ({ ...p, role: v }))} options={["Admin","Deal","Task"]} />
          <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={editForm.isInternal} onChange={e => setEditForm(p => ({ ...p, isInternal: e.target.checked }))} /> Internal user
            </label>
            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm(p => ({ ...p, isActive: e.target.checked }))} /> Active
            </label>
          </div>

          {/* ── SMS ─────────────────────────────────────────────── */}
          <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 14, marginBottom: 14 }}>
            <div style={{ marginBottom: 10 }}><FieldLabel>SMS (Twilio)</FieldLabel></div>
            <Input
              label="Phone Number"
              type="tel"
              value={editForm.phoneNumber || ''}
              onChange={v => setEditForm(p => ({ ...p, phoneNumber: v }))}
              placeholder="+15551234567"
            />
            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 8 }}>
              <input
                type="checkbox"
                checked={!!editForm.smsEnabled}
                onChange={e => setEditForm(p => ({ ...p, smsEnabled: e.target.checked }))}
              /> Enable SMS bot
            </label>
          </div>

          {/* ── Microsoft Teams ──────────────────────────────────── */}
          <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 14, marginBottom: 14 }}>
            <div style={{ marginBottom: 10 }}><FieldLabel>Microsoft Teams</FieldLabel></div>
            <div style={{ padding: '10px 12px', borderRadius: radius.md, background: editForm.teamsUserId ? B.green + '14' : B.surfaceMuted, border: `1px solid ${editForm.teamsUserId ? B.green + '33' : B.border}`, fontSize: 13 }}>
              {editForm.teamsUserId ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: B.green, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="check" size={14} /> Registered</span>
                  <span style={{ color: B.textMd }}>— Teams DM notifications active</span>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4, color: B.text }}>Not registered yet</div>
                  <div style={{ color: B.textMd, lineHeight: 1.5 }}>
                    To enable Teams notifications, have this user message the Harvus bot in Teams with:
                    <span style={{ display: 'inline-block', background: B.navy, color: '#fff', borderRadius: 4, padding: '1px 8px', marginLeft: 6, fontFamily: 'monospace', fontSize: 12 }}>register</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <ModalFooter>
            <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
            <Btn disabled={saving} onClick={saveEdit}>{saving ? "Saving…" : "Save Changes"}</Btn>
          </ModalFooter>
        </Modal>
      )}

      {/* Add User Manually */}
      {showEntra && (
        <Modal title="Add User" onClose={() => setShowEntra(false)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Input label="Display Name" value={newForm.displayName || ""} onChange={v => setNewForm(p => ({ ...p, displayName: v }))} />
            <Input label="Email" value={newForm.email || ""} onChange={v => setNewForm(p => ({ ...p, email: v }))} />
            <Select label="Role" value={newForm.role || "Task"} onChange={v => setNewForm(p => ({ ...p, role: v }))}
              options={[{ value: "Admin", label: "Admin" }, { value: "Deal", label: "Deal" }, { value: "Task", label: "Task" }]} />
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={!!newForm.isInternal} onChange={e => setNewForm(p => ({ ...p, isInternal: e.target.checked }))} />
              <span style={{ fontSize: 13 }}>Internal (Harvus staff)</span>
            </div>
            <Btn onClick={async () => {
              if (!newForm.displayName?.trim() || !newForm.email?.trim()) return;
              try {
                await api.post("users", {
                  displayName: newForm.displayName.trim(),
                  email: newForm.email.trim(),
                  externalId: newForm.email.trim(),
                  role: newForm.role || "Task",
                  isInternal: !!newForm.isInternal,
                });
                setNewForm({});
                reload();
                setShowEntra(false);
              } catch(e) { toast.error("Error adding user: " + e.message); }
            }}>Add User</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};
